import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Gift, Copy as CopyIcon, Share2, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

/**
 * ReferralCard — member dashboard widget.
 *
 *  • Shows the member's unique referral link with a copy button.
 *  • Stats: friends referred, friends converted, free months earned (incl. pending credits).
 *  • Share button uses Web Share API where available, else copies a pre-filled message.
 */
export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/referrals/me');
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load your referral info');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-card p-6 border border-border-subtle">
        <div className="flex items-center gap-2 text-text-mid text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading referral…
        </div>
      </div>
    );
  }
  if (!data) return null;

  const message = `I found my people on Cali Moms Connect — join me! Use my link to sign up: ${data.link}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — long-press the link to copy');
    }
  };

  const share = async () => {
    setSharing(true);
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Cali Moms Connect',
          text: message,
        });
      } else {
        await navigator.clipboard.writeText(message);
        toast.success('Share message copied to your clipboard');
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(message);
          toast.success('Share message copied to your clipboard');
        } catch {/* ignore */}
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <div
      className="bg-gradient-to-br from-teal/10 via-seafoam/20 to-white rounded-2xl shadow-card p-6 border border-teal/20"
      data-testid="referral-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-teal/15 flex items-center justify-center shrink-0">
          <Gift className="text-teal" size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg text-navy">Refer a Friend</h3>
          <p className="text-xs text-text-mid mt-0.5">
            Share your link — when a friend joins Thrive, you earn a free month.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border-subtle p-3 flex items-center gap-2 mb-4">
        <code className="flex-1 text-xs text-navy font-mono truncate" data-testid="referral-link">
          {data.link}
        </code>
        <Button
          size="sm"
          onClick={copyLink}
          className="bg-teal text-white hover:bg-teal-deep rounded-full h-8 px-3 shrink-0"
          data-testid="copy-referral-link-btn"
        >
          {copied ? <Check size={14} /> : <CopyIcon size={14} />}
          <span className="ml-1 text-xs">{copied ? 'Copied' : 'Copy'}</span>
        </Button>
      </div>

      <Button
        onClick={share}
        disabled={sharing}
        variant="outline"
        className="w-full rounded-full border-teal text-teal-deep hover:bg-teal hover:text-white"
        data-testid="share-referral-btn"
      >
        {sharing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Share2 size={14} className="mr-2" />}
        Share with a friend
      </Button>

      <div className="grid grid-cols-3 gap-2 mt-5 text-center">
        <div data-testid="referral-stat-referred">
          <p className="text-2xl font-bold text-navy">{data.referred_count}</p>
          <p className="text-[11px] text-text-mid">Referred</p>
        </div>
        <div data-testid="referral-stat-converted">
          <p className="text-2xl font-bold text-navy">{data.converted_count}</p>
          <p className="text-[11px] text-text-mid">Joined Thrive</p>
        </div>
        <div data-testid="referral-stat-months">
          <p className="text-2xl font-bold text-teal-deep">{data.free_months_earned}</p>
          <p className="text-[11px] text-text-mid">Free Months</p>
        </div>
      </div>
      {data.pending_free_months > 0 && (
        <p className="text-[11px] text-text-mid text-center mt-2" data-testid="referral-pending-note">
          {data.pending_free_months} pending credit{data.pending_free_months === 1 ? '' : 's'} — applied when you upgrade to Thrive.
        </p>
      )}
    </div>
  );
}
